import { focusManager, QueryClient, hydrate, dehydrate } from '@tanstack/react-query'
import { ApiError } from '@/api/http'
import { onBridgeEvent } from '@/bridge'

const CACHE_KEY = 'yolo-ff-qcache'
const MAX_AGE = 24 * 60 * 60 * 1000

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: MAX_AGE,
      retry: (count, error) => {
        // Client errors won't fix themselves on retry.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
        return count < 2
      },
    },
    mutations: { retry: false },
  },
})

// Synchronously hydrate cache before React renders — no isRestoring delay, instant data on revisit.
// Skips 'bridge' queries so SessionGate always calls getSession fresh on mount.
try {
  const raw = window.localStorage.getItem(CACHE_KEY)
  if (raw) {
    const { timestamp, clientState } = JSON.parse(raw)
    if (typeof timestamp === 'number' && Date.now() - timestamp < MAX_AGE && clientState) {
      const filtered = {
        ...clientState,
        queries: (clientState.queries ?? []).filter(
          (q: { queryKey: unknown[] }) => q.queryKey[0] !== 'bridge'
        ),
      }
      hydrate(queryClient, filtered)
    }
  }
} catch {
  /* no-op */
}

// Write cache to localStorage on change, throttled to once per 3s.
// Exclude non-API queries (bridge/session) — those must refetch on every mount.
let persistTimer: ReturnType<typeof setTimeout> | null = null
queryClient.getQueryCache().subscribe(() => {
  if (persistTimer) clearTimeout(persistTimer)
  persistTimer = setTimeout(() => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        timestamp: Date.now(),
        clientState: dehydrate(queryClient, {
          shouldDehydrateQuery: (query) =>
            query.state.status === 'success' && query.queryKey[0] !== 'bridge',
        }),
      }))
    } catch { /* localStorage write failed (quota/private mode) — ignore */ }
  }, 3_000)
})

// WebView visibility events are unreliable; native tells us when the app resumes.
focusManager.setEventListener((setFocused) => {
  const onVisibility = () => setFocused(document.visibilityState === 'visible')
  document.addEventListener('visibilitychange', onVisibility)
  const offResume = onBridgeEvent('resumed', () => setFocused(true))
  const offPause = onBridgeEvent('paused', () => setFocused(false))
  return () => {
    document.removeEventListener('visibilitychange', onVisibility)
    offResume()
    offPause()
  }
})
